import { useLocation } from '@remix-run/react'
import clsx from 'clsx'
import {
  LucideChevronFirst,
  LucideChevronLeft,
  LucideChevronRight,
  LucideChevronsRight,
} from 'lucide-react'
import { useCallback, useEffect, useMemo } from 'react'
import {
  aCardsCollapse,
  aHoverFeature,
  aMapCurrentCount,
  aSelectedListing,
  useAtom,
  useAtomValue,
  useSetAtom,
} from '~/atoms'
import { ClientOnly, ControlTooltip } from '~/components/common'
import { Button } from '~/components/ui'
import { useBreakpoint } from '~/hooks'
import { type Listing } from '~/models'
import { cn, debounce, fmtAddress } from '~/utils'
import MapListingCard from './Card'
import MapListingCardSkeleton from './CardSkeleton'

export default function MapCardList({
  className,
  listings,
  isLoading,
  isFetching,
  fetchNextPage,
  hasNextPage,
  showOriginalAttachments = false,
  showOriginalBrokers = false,
}: {
  className?: string
  listings: Listing[]
  reportSelectedListing?: Listing | null
  isLoading?: boolean
  isFetching?: boolean
  fetchNextPage?: () => void
  hasNextPage?: boolean
  showOriginalAttachments?: boolean
  showOriginalBrokers?: boolean
}) {
  const location = useLocation()
  const currentMapCount = useAtomValue(aMapCurrentCount)
  const setHoverFeature = useSetAtom(aHoverFeature)
  const selectedListing = useAtomValue(aSelectedListing)
  const [cardCollapse, setCardCollapse] = useAtom(aCardsCollapse)
  const { xlDown } = useBreakpoint()

  useEffect(() => {
    const elem = document.getElementById('cards-list')
    if (!fetchNextPage) {
      return
    }
    const onScroll = () => {
      if (
        hasNextPage &&
        !isFetching &&
        elem!.scrollTop > elem!.scrollHeight - elem!.scrollHeight / 4
      ) {
        fetchNextPage()
      }
    }
    const debouncedOnScroll = debounce(onScroll, 100)
    elem!.addEventListener('scroll', debouncedOnScroll)
    return () => elem!.removeEventListener('scroll', debouncedOnScroll)
  }, [hasNextPage, isFetching, fetchNextPage])

  const isReportPage = useMemo(() => {
    return location.pathname.includes('/report')
  }, [location])

  const loadingCount = useMemo(() => {
    if (currentMapCount === 0 || (selectedListing && currentMapCount === 1)) {
      return 0
    }
    return 8
  }, [currentMapCount, selectedListing])

  const onCollapseClick = useCallback(() => {
    if (cardCollapse === 'double') {
      if (xlDown) {
        setCardCollapse('none')
      } else {
        setCardCollapse('single')
      }
    } else if (cardCollapse === 'single') {
      setCardCollapse('none')
    } else if (!xlDown) {
      setCardCollapse('double')
    } else {
      setCardCollapse('single')
    }
  }, [cardCollapse, setCardCollapse, xlDown])

  const collapseText = useMemo(() => {
    if (cardCollapse === 'none') {
      return 'Show Cards'
    } else if (cardCollapse === 'single') {
      return 'Hide Cards'
    }
    return 'Show Fewer Cards'
  }, [cardCollapse, xlDown])

  const collapseIcon = useMemo(() => {
    if (cardCollapse === 'none') {
      if (xlDown) {
        return <LucideChevronRight className="h-6 w-6" />
      }
      return <LucideChevronsRight className="h-6 w-6" />
    } else if (
      cardCollapse === 'single' ||
      (cardCollapse === 'double' && xlDown)
    ) {
      return <LucideChevronFirst className="h-6 w-6" />
    }
    return <LucideChevronLeft className="h-6 w-6" />
  }, [cardCollapse, xlDown])

  return (
    <div className={cn(className, 'relative hidden lg:flex')}>
      <div
        id="cards-list"
        className={cn(
          'z-40 grid w-full place-content-start gap-4 overflow-y-auto overflow-x-hidden pb-4',
          isReportPage
            ? 'lg:grid-cols-1 2xl:grid-cols-2'
            : {
                'lg:grid-cols-1 2xl:grid-cols-2':
                  cardCollapse === 'double' &&
                  (listings.length > 1 || isLoading),
                'lg:grid-cols-1':
                  cardCollapse === 'single' ||
                  (listings.length <= 1 && !isLoading),
                hidden: cardCollapse === 'none',
              }
        )}>
        {isLoading && listings.length === 0
          ? Array.from({ length: loadingCount }, (_, index) => index).map(
              (index) => {
                return (
                  <MapListingCardSkeleton
                    key={index}
                    className={clsx('md:w-[350px] 2xl:w-[350px]')}
                  />
                )
              }
            )
          : listings.map((listing) => {
              return (
                <MapListingCard
                  key={listing._id}
                  className={clsx(
                    isFetching && 'animate-pulse',
                    'md:w-[350px] 2xl:w-[350px]'
                    // loadingCount < 4 && 'col-span-2'
                  )}
                  showOriginalAttachments={showOriginalAttachments}
                  showOriginalBrokers={showOriginalBrokers}
                  listing={listing}
                  onMouseOver={() => {
                    if (!selectedListing)
                      setHoverFeature({
                        address: listing.isBusinessListing
                          ? fmtAddress(listing.property, {
                              locationVisibility: listing.locationVisibility,
                            })
                          : listing.property.address,
                        isBusinessListing:
                          (listing.isBusinessListing as unknown as boolean) ||
                          false,
                        lat: listing.property.latitude,
                        lng: listing.property.longitude,
                        name: listing.name,
                        propertyTypes: listing.propertyTypes.join(', '),
                        status: listing.status,
                        type: listing.type,
                        _id: listing._id,
                      })
                  }}
                  onMouseOut={() => {
                    setHoverFeature(null)
                  }}
                />
              )
            })}
        {hasNextPage && fetchNextPage && (
          <div className="col-span-full flex justify-center">
            <Button disabled={isFetching} onClick={() => fetchNextPage()}>
              {isFetching ? 'Loading' : 'Load More'}
            </Button>
          </div>
        )}
      </div>

      {!isReportPage && (
        <ClientOnly>
          <ControlTooltip side="right" text={collapseText}>
            <Button
              variant="map"
              size="map"
              className={cn(
                'fixed left-4 top-4 z-50'
                // currentMapCount === 0 && 'hidden'
              )}
              onClick={onCollapseClick}>
              <span className="sr-only">Collapse Cards</span>
              {collapseIcon}
            </Button>
          </ControlTooltip>
        </ClientOnly>
      )}
    </div>
  )
}
